import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Loader2, ChevronDown, X } from "lucide-react";

const AddEmployee = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "osmc2025",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip_code: "",
    date_of_hire: "",
    positions: [],
  });

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${API}/positions`, {
        headers: getAuthHeaders(),
        
      });
      setPositions(response.data);
    } catch (error) {
      console.error("Error fetching positions:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePositionToggle = (positionName) => {
    setFormData((prev) => {
      const current = prev.positions;
      if (current.includes(positionName)) {
        return { ...prev, positions: current.filter((p) => p !== positionName) };
      } else {
        return { ...prev, positions: [...current, positionName] };
      }
    });
  };

  const removePosition = (positionName) => {
    setFormData((prev) => ({
      ...prev,
      positions: prev.positions.filter((p) => p !== positionName),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/employees`, formData, {
        headers: getAuthHeaders(),
        
      });
      toast.success("Employee added successfully!");
      navigate("/employee-list");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to add employee");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="add-employee-page">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-[#666666] hover:text-[#1A1A1A]"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Add Employee</h1>
          <p className="text-[#666666]">Create a new employee account</p>
        </div>
      </div>

      <Card className="border border-[#E5E5E5] shadow-none max-w-2xl">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <UserPlus className="w-5 h-5 text-[#6A1E2E]" />
            Employee Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-employee-form">
            {/* Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-last-name"
                />
              </div>
            </div>

            {/* Contact Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-phone"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                data-testid="input-password"
              />
            </div>

            {/* Address Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="street">Street</Label>
                <Input
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="123 Main St"
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-street"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-city"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="State"
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-state"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">Zip Code</Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  placeholder="12345"
                  className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                  data-testid="input-zip"
                />
              </div>
            </div>

            {/* Date of Hire */}
            <div className="space-y-2">
              <Label htmlFor="date_of_hire">Date of Hire</Label>
              <Input
                id="date_of_hire"
                name="date_of_hire"
                type="date"
                value={formData.date_of_hire}
                onChange={handleInputChange}
                className="border-[#E5E5E5] focus:border-[#6A1E2E]"
                data-testid="input-date-of-hire"
              />
            </div>

            {/* Positions - Dropdown Multi-select */}
            <div className="space-y-2">
              <Label>Positions Able to Work</Label>
              <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between border-[#E5E5E5] hover:bg-[#FAFAFA]"
                    data-testid="positions-dropdown"
                  >
                    <span className="text-[#666666]">
                      {formData.positions.length === 0
                        ? "Select positions..."
                        : `${formData.positions.length} position(s) selected`}
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#999999]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[300px] max-h-[300px] overflow-y-auto">
                  {positions.length === 0 ? (
                    <div className="p-3 text-sm text-[#999999]">
                      No positions available. Add positions in Settings.
                    </div>
                  ) : (
                    positions.map((pos) => (
                      <DropdownMenuCheckboxItem
                        key={pos.id}
                        checked={formData.positions.includes(pos.name)}
                        onCheckedChange={() => handlePositionToggle(pos.name)}
                        data-testid={`position-option-${pos.name.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {pos.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Selected Positions Tags */}
              {formData.positions.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.positions.map((pos) => (
                    <span
                      key={pos}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-[#6A1E2E] text-white text-sm rounded-sm"
                    >
                      {pos}
                      <button
                        type="button"
                        onClick={() => removePosition(pos)}
                        className="hover:bg-white/20 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="border-[#E5E5E5]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
                data-testid="submit-employee"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Employee"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddEmployee;
